import { getAccessToken } from '#lib/authToken';

const BACKEND = process.env.REACT_APP_BACKEND_WORKER_URL;

/**
 * Uploads an image to the post-mailing media endpoint and returns its id + public URL.
 *
 * This bypasses the shared `fetcher`, which always sends JSON — image upload needs a
 * multipart/form-data body. The JWT is injected manually; Content-Type is intentionally
 * left unset so the browser adds the multipart boundary. On failure, throws an Error
 * carrying the backend `code`/`status` so callers can translate it like any other error.
 *
 * @param {File|Blob} file - The image file to upload (JPEG, PNG or WebP, ≤ 5 MB).
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional abort signal to cancel the upload.
 * @returns {Promise<{ id: string, url: string }>} The media id and its public URL.
 * @throws {Error} With `status` and `code` set on a non-successful response.
 */
export async function uploadPostMailingImage(file, { signal } = {}) {
	const token = await getAccessToken();

	const form = new FormData();
	form.append('image', file);

	const res = await fetch(`${BACKEND}/api/v1/post-mailing/media`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: form,
		signal,
	});

	if (!res.ok) {
		let errorData = {};
		try {
			errorData = await res.json();
		} catch {
			errorData = { errorMessage: `Upload failed: ${res.status}` };
		}
		const error = new Error(errorData.errorMessage || `Upload failed: ${res.status}`);
		error.status = res.status;
		error.code = errorData.errorCode ?? null;
		error.validationErrors = errorData.validationErrors ?? null;
		throw error;
	}

	return res.json();
}

/**
 * Uploads a finished A4 letter-side PDF (front or back page) to the post-mailing media endpoint and
 * returns its private asset id. Same multipart/manual-JWT approach as uploadPostMailingImage (the shared
 * `fetcher` only sends JSON). No URL is returned — the asset is private; the worker reads it by id.
 *
 * @param {File|Blob} file - The PDF file to upload (1-page A4, ≤ 10 MB).
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional abort signal to cancel the upload.
 * @returns {Promise<{ id: string, sizeBytes: number, originalFilename: string }>} The stored asset id + metadata.
 * @throws {Error} With `status` and `code` set on a non-successful response.
 */
export async function uploadPostMailingPdf(file, { signal } = {}) {
	const token = await getAccessToken();

	const form = new FormData();
	form.append('pdf', file);

	const res = await fetch(`${BACKEND}/api/v1/post-mailing/media/pdf`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: form,
		signal,
	});

	if (!res.ok) {
		let errorData = {};
		try {
			errorData = await res.json();
		} catch {
			errorData = { errorMessage: `Upload failed: ${res.status}` };
		}
		const error = new Error(errorData.errorMessage || `Upload failed: ${res.status}`);
		error.status = res.status;
		error.code = errorData.errorCode ?? null;
		error.validationErrors = errorData.validationErrors ?? null;
		throw error;
	}

	return res.json();
}
